import React, { useState } from 'react';
import { 
    Box, 
    Grid,
    Typography,
    Button,
    Badge,
    IconButton
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as IncompleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

const FormNavigator = ({ 
    forms, 
    activeFormId, 
    onFormSelect,
    completionStatus,
    height = 120,
    expandedColumns = {},
    onExpandToggle
}) => {
    // Group forms by type
    const groupedForms = {
        // Show 'auto' under the main column per UX request
        main: forms.filter(f => f.type === 'main' || f.type === 'auto'),
        medical: forms.filter(f => f.type === 'medical'),
        drugtest: forms.filter(f => f.type === 'drugtest'),
        witness: forms.filter(f => f.type === 'witness')
    };
    
    // Sort each group: incomplete first, then by name
    Object.keys(groupedForms).forEach(key => {
        if (key !== 'main') { // Don't sort main since it's always single
            groupedForms[key].sort((a, b) => {
                const aComplete = completionStatus[a.id] || false;
                const bComplete = completionStatus[b.id] || false;
                if (aComplete !== bComplete) {
                    return aComplete ? 1 : -1; // Incomplete first
                }
                // If same completion status, sort by name
                const aName = a.subtitle || '';
                const bName = b.subtitle || '';
                return aName.localeCompare(bName);
            });
        }
    });

    const getColumnTitle = (type) => {
        switch(type) {
            case 'main': return 'Full Report';
            case 'medical': return 'Medical Refusal';
            case 'drugtest': return 'Drug Test';
            case 'witness': return 'Witness';
            default: return '';
        }
    };

    const renderFormButton = (form) => {
        const isComplete = completionStatus[form.id] || false;
        const isActive = form.id === activeFormId;
        
        return (
            <Button
                key={form.id}
                onClick={() => onFormSelect(form.id)}
                variant={isActive ? "contained" : "outlined"}
                size="small"
                fullWidth
                sx={{
                    minHeight: 28,
                    maxHeight: 28,
                    mb: 0.25,
                    justifyContent: 'flex-start',
                    px: 1,
                    backgroundColor: isActive ? 
                        (isComplete ? '#2e7d32' : '#ed6c02') : 
                        'transparent',
                    borderColor: isComplete ? '#2e7d32' : '#ed6c02',
                    color: isActive ? 'white' : (isComplete ? '#2e7d32' : '#ed6c02'),
                    '&:hover': {
                        backgroundColor: isActive ? 
                            (isComplete ? '#1b5e20' : '#e65100') :
                            (isComplete ? 'rgba(46, 125, 50, 0.04)' : 'rgba(237, 108, 2, 0.04)'),
                        borderColor: isComplete ? '#1b5e20' : '#e65100'
                    },
                    fontSize: '0.7rem',
                    fontWeight: isActive ? 600 : 400
                }}
                startIcon={
                    isComplete ? 
                        <CheckCircleIcon sx={{ fontSize: 16 }} /> : 
                        <IncompleteIcon sx={{ fontSize: 16 }} />
                }
            >
                <Box sx={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    textAlign: 'left'
                }}>
                    {form.type === 'main' ? 'Main Form' : (form.subtitle || form.label || 'Form')}
                </Box>
            </Button>
        );
    };
    
    const toggleExpanded = (type) => {
        if (onExpandToggle) {
            onExpandToggle(type, !expandedColumns[type]);
        }
    };
    
    const renderColumn = (type, formList) => {
        const isExpanded = expandedColumns[type] || false;
        const hasMoreThan3 = formList.length > 3;
        const visibleForms = (hasMoreThan3 && !isExpanded) ? formList.slice(0, 3) : formList;
        const incompleteCount = formList.filter(f => !completionStatus[f.id]).length;
        
        return (
            <Grid item xs={3} key={type}>
                <Box sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: type !== 'witness' ? 1 : 0,
                    borderColor: 'divider'
                }}>
                    {/* Column Header */}
                    <Box sx={{ 
                        p: 0.25, 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        backgroundColor: 'grey.50',
                        minHeight: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 0.5
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flex: 1 }}>
                            <Typography variant="caption" sx={{ 
                                fontWeight: 600, 
                                fontSize: '0.7rem',
                                color: 'text.secondary'
                            }}>
                                {getColumnTitle(type)}
                            </Typography>
                            {formList.length > 0 && incompleteCount > 0 && (
                                <Badge 
                                    badgeContent={incompleteCount} 
                                    color="warning"
                                    sx={{ 
                                        '& .MuiBadge-badge': { 
                                            fontSize: '0.6rem',
                                            height: 14,
                                            minWidth: 14,
                                            padding: 0,
                                            position: 'static',
                                            transform: 'none'
                                        }
                                    }}
                                />
                            )}
                        </Box>
                        {hasMoreThan3 && (
                            <IconButton
                                size="small"
                                onClick={() => toggleExpanded(type)}
                                sx={{ 
                                    padding: 0,
                                    width: 18,
                                    height: 18,
                                    color: 'action.active'
                                }}
                            >
                                {isExpanded ? 
                                    <ExpandLessIcon sx={{ fontSize: 14 }} /> : 
                                    <ExpandMoreIcon sx={{ fontSize: 14 }} />
                                }
                            </IconButton>
                        )}
                    </Box>
                    
                    {/* Form Buttons */}
                    <Box sx={{ 
                        flex: 1,
                        p: 0.25,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        '&::-webkit-scrollbar': {
                            width: 4,
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#888',
                            borderRadius: 2,
                        }
                    }}>
                        {formList.length > 0 ? (
                            visibleForms.map(form => renderFormButton(form))
                        ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ 
                                fontSize: '0.65rem',
                                display: 'block',
                                textAlign: 'center',
                                mt: 1
                            }}>
                                None
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Grid>
        );
    };

    return (
        <Box sx={{ 
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            height: height,
            overflow: 'hidden'
        }}>
            <Grid container sx={{ height: '100%' }}>
                {renderColumn('main', groupedForms.main)}
                {renderColumn('medical', groupedForms.medical)}
                {renderColumn('drugtest', groupedForms.drugtest)}
                {renderColumn('witness', groupedForms.witness)}
            </Grid>
        </Box>
    );
};

export default FormNavigator;
